'use client';

import { variants } from '@/components/sections/builder/theme/typography';
import { DEFAULT_THEME_LAYOUT } from '@/components/sections/builder/theme/layout';
import {
  CommonElementDataType,
  ElementType,
  ThemeBlogCardType,
  ThemeCollectionType,
  ThemeProductCardType,
  ThemeType,
  ThemeTypographyType,
  ThemeVariantPillType,
  useBuilderStore,
} from '@/stores/builder';
import clsx from 'clsx';
import { useDrag } from 'react-dnd';
import { useEffect, useMemo } from 'react';
import AOS from 'aos';

import 'aos/dist/aos.css';
import { DEFAULT_THEME_ANIMATION } from '@/components/sections/builder/theme/animation';
import { DEFAULT_THEME_DROPDOWN_POPUP } from '@/components/sections/builder/theme/dropdown-popup';
import { DEFAULT_THEME_SEARCH_BEHAVIOR } from '@/components/sections/builder/theme/search-behavior';
import { DEFAULT_THEME_BUTTON } from '@/components/sections/builder/theme/button';
import { shallow } from 'zustand/shallow';
import { DEFAULT_THEME_COLLECTION } from '@/components/sections/builder/theme/collection';
import { DEFAULT_THEME_PRODUCT_CARD } from '@/components/sections/builder/theme/product-card';
import { DEFAULT_THEME_BLOG_CARD } from '@/components/sections/builder/theme/blog-card';
import { DEFAULT_THEME_INPUT } from '@/components/sections/builder/theme/input';
import { DEFAULT_THEME_VARIANT_PILL } from '@/components/sections/builder/theme/variant-pill';
import { DEFAULT_THEME_MEDIA } from '@/components/sections/builder/theme/media';
import { DEFAULT_THEME_CART } from '@/components/sections/builder/theme/cart';
import { ElementListType } from '@/components/sections/builder/types';
import { ElementComponents } from '@/components/sections/builder/builder-element';
import { DEFAULT_THEME_BADGE } from '@/components/sections/builder/theme/badge';
import { cn } from '@/lib/utils';
import { DEFAULT_THEME_DRAWER } from '@/components/sections/builder/theme/drawer';
import { DEFAULT_THEME_COLOR_SCHEME } from '@/components/sections/builder/theme/color';

export const WrapperMenu = ({
  element,
  onClick,
  children,
  disabled,
}: {
  element: ElementListType;
  onClick?: (element: ElementType) => void;
  children: React.ReactNode;
  disabled?: boolean;
}) => {
  const childs = (() => {
    if (element.defaultChilds?.length) return element.defaultChilds;
    return element.childs || [];
  })();

  const item = {
    id: '',
    type: element.type,
    group: element.group as string,
    data: element.defaultData,
    childs: childs.map((child) => ({
      id: '',
      type: child.type,
      group: child.group as string,
      data: child.defaultData,
    })),
  };

  const [, drag] = useDrag(
    () => ({
      type: element.type,
      item,
    }),
    []
  );

  return (
    <button
      ref={drag as unknown as React.Ref<HTMLButtonElement>}
      type='button'
      onClick={() => {
        onClick?.(item);
      }}
      className={clsx(
        'rounded-md border border-white hover:bg-gray-200',
        disabled ? 'cursor-not-allowed bg-gray-200 text-gray-400' : 'bg-white'
      )}
      disabled={disabled}
    >
      {children}
    </button>
  );
};

type WrapperPreviewType = {
  group: string;
  title: string;
  minHeight: number;
  classNameWrapper?: string;
  data?: unknown;
};

export default function WrapperPreview({
  group,
  title,
  minHeight,
  classNameWrapper,
  data,
}: WrapperPreviewType) {
  const builder = useBuilderStore(
    (state) => ({
      elements: state.elements,
      draggingId: state.draggingId,
      appendElement: state.appendElement,
    }),
    shallow
  );

  const groups = group.split(',');

  const elements = builder.elements
    ?.filter((element) => !(element.data as CommonElementDataType)?.hidden)
    ?.filter((element) => !!ElementComponents[element.type]?.Element)
    ?.filter((element) => groups.includes(element.group))
    ?.map((element) => ({
      ...element,
      childs: element.childs?.filter((child) => !(child.data as CommonElementDataType)?.hidden),
    }));

  return (
    <>
      <div
        id={`wrapper-${group}`}
        className={cn('relative w-full', classNameWrapper, group === 'body' && 'flex-grow')}
        style={{ minHeight: `${minHeight}px` }}
      >
        <div
          className={cn(
            ' w-full',
            groups.includes('body') && '',
            groups.includes('body_cart') && 'theme_cart_page_bg'
          )}
        >
          {elements?.map((element) => (
            <WrapperElement key={element.id} element={element} data={data} />
          ))}
        </div>
      </div>
      <style jsx>
        {`
          .wrapper-element-active-${group}::after {
            content: '${title}';
          }
        `}
      </style>
    </>
  );
}

export const WrapperElement = ({ element, data }: { element: ElementType; data?: unknown }) => {
  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      elements: state.elements,
      draggingId: state.draggingId,
      selectedId: state.selectedId,
      selectedParentId: state.selectedParentId,
      moveElement: state.moveElement,
      appendElement: state.appendElement,
      setSelected: state.setSelected,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const Element = ElementComponents[element.type].Element;

  return (
    <>
      <div
        role='button'
        onFocus={(e) => {
          e.stopPropagation();
          builder.setSelected(element.id);
        }}
        onClick={() => {
          builder.setSelected(element.id);
          window?.top?.postMessage(
            {
              type: 'select_element',
              elements: builder.elements,
              theme: builder.theme,
              selectedId: element.id,
              draggingId: builder.draggingId,
              selectedParentId: builder.selectedParentId,
            },
            '*'
          );
        }}
        id={`view-${element.id}`}
        className={clsx(
          `view-${element.id}`,
          'wrapper-element-btn relative block w-full',
          [builder.draggingId, builder.selectedId, builder.selectedParentId].includes(element.id)
            ? 'wrapper-element-active'
            : 'wrapper-element'
        )}
      >
        <div className='w-full'>
          {Element && (
            <Element
              element={element}
              preview
              selectedId={builder.selectedId}
              theme={builder.theme}
              data={data}
            />
          )}
        </div>
      </div>
      <style jsx>
        {`
          .view-${element.id}:hover::after,
            .view-${element.id}:focus::after,
            .wrapper-element-active::after {
            content: '${(element.data as CommonElementDataType).name}';
          }
          .wrapper-element-btn:has(.slideshow-full-width) {
            position: relative;
            left: 50%;
            right: 50%;
            margin-left: -50vw;
            margin-right: -50vw;
            width: 100vw;
          }
        `}
      </style>
    </>
  );
};

export const WrapperChild = ({
  child,
  parentType,
  className,
  preview,
  isUseWrapperClass,
  data,
}: {
  child: ElementType;
  parentType: string;
  className?: string;
  preview?: boolean;
  isUseWrapperClass?: boolean;
  data?: unknown;
}) => {
  const builder = useBuilderStore(
    (state) => ({
      setSelected: state.setSelected,
      selectedId: state.selectedId,
      draggingId: state.draggingId,
    }),
    shallow
  );

  const Element = (ElementComponents[parentType]?.childs || []).find(
    (v) => v.type === child.type
  )?.Element;

  if (!Element) return <></>;

  if (!preview) {
    if (isUseWrapperClass) {
      return (
        <div className={className}>
          <Element element={child} data={data} />
        </div>
      );
    }
    return <Element element={child} data={data} />;
  }

  return (
    <>
      <div
        role='button'
        id={`view-child-${child.id}`}
        onFocus={(e) => {
          e.stopPropagation();
          builder.setSelected(child.id);
        }}
        className={clsx(
          'border border-dashed border-transparent hover:border-primary focus:border-primary',
          builder.selectedId === child.id && '!border-primary',
          builder.draggingId === child.id && '!border-primary',
          `child-${child.id}`,
          className
        )}
      >
        <Element element={child} data={data} preview />
      </div>
    </>
  );
};

export const WrapperTheme = ({
  children,
  theme,
}: {
  children: React.ReactNode;
  theme: ThemeType;
}) => {
  const themeLayout = theme?.layout || DEFAULT_THEME_LAYOUT;
  const themeAnimation = theme?.animation || DEFAULT_THEME_ANIMATION;
  const themeDropdownPopup = theme?.dropdownPopup || DEFAULT_THEME_DROPDOWN_POPUP;
  const themeSearchBehavior = theme?.searchBehavior || DEFAULT_THEME_SEARCH_BEHAVIOR;
  const themeCart = theme?.cart || DEFAULT_THEME_CART;
  const themeBadge = theme?.badge || DEFAULT_THEME_BADGE;
  const themeModalProductDetail = theme?.colorSchemeModalDetail || DEFAULT_THEME_COLOR_SCHEME;

  // Apply data attribute for animation on scroll element
  const applyAosAttribute = () => {
    const elements: Element[] = Array.from(document.querySelectorAll('[data-animation="true"]'));
    const animationGroup = elements.reduce((groups, element) => {
      const group = element.getAttribute('data-animation-group') || 'none';
      if (!groups[group]) {
        groups[group] = [];
      }
      groups[group].push(element);
      return groups;
    }, {} as Record<string, Element[]>);

    const delay = themeAnimation.animationDelay;
    const type = themeAnimation.animationType;

    Object.entries(animationGroup).forEach(([key, values]) => {
      values?.forEach((element, i) => {
        if (element.hasAttribute('data-aos-override')) return;

        if (element.getAttribute('data-aos') !== type) {
          element.setAttribute('data-aos', type);
        }

        const delayValue = key === 'none' ? `0` : `${i * delay + delay}`;
        if (element.getAttribute('data-aos-delay') !== delayValue) {
          element.setAttribute('data-aos-delay', delayValue);
        }
      });
    });
  };

  useEffect(() => {
    AOS.init({ once: false });

    if (themeAnimation.animateOnScroll) {
      let interval: NodeJS.Timer;
      let retry = 0;
      // eslint-disable-next-line prefer-const
      interval = setInterval(() => {
        if (retry > 3) return clearInterval(interval);
        applyAosAttribute();

        retry++;
        AOS.refreshHard();
      }, 1000);
    }
  }, []);

  useEffect(() => {
    if (themeAnimation.animateOnScroll) {
      applyAosAttribute();
      AOS.refreshHard();
    } else {
      const elements = document.querySelectorAll('[data-animation="true"]');
      elements?.forEach((element) => {
        if (element.hasAttribute('data-aos-override')) return;
        element.removeAttribute('data-aos');
        element.removeAttribute('data-aos-delay');
      });
    }
  }, [themeAnimation.animateOnScroll, themeAnimation.animationDelay, themeAnimation.animationType]);

  const themeButton = theme?.button || DEFAULT_THEME_BUTTON;
  const themeInput = theme?.input || DEFAULT_THEME_INPUT;
  const themeVariantPill: ThemeVariantPillType = theme?.variantPill || DEFAULT_THEME_VARIANT_PILL;
  const themeCollection: ThemeCollectionType = theme?.collection || DEFAULT_THEME_COLLECTION;
  const themeProductCard: ThemeProductCardType = theme?.productCard || DEFAULT_THEME_PRODUCT_CARD;
  const themeBlogCard: ThemeBlogCardType = theme?.blogCard || DEFAULT_THEME_BLOG_CARD;
  const themeDrawer = theme?.drawer || DEFAULT_THEME_DRAWER;
  const themeTypography: ThemeTypographyType = theme?.typography || {};

  const themeMedia = theme?.media || DEFAULT_THEME_MEDIA;

  const headingVariantValue = useMemo(() => {
    return variants[themeTypography?.headingFontVariant as string] || variants.regular;
  }, [themeTypography?.headingFontVariant]);
  const variantValue = useMemo(() => {
    return variants[themeTypography?.fontVariant as string] || variants.regular;
  }, [themeTypography?.fontVariant]);

  const buildFontsHref = (heading?: string, body?: string) => {
    const weights = '400;500;600;700';
    const toParam = (name: string) => `family=${encodeURIComponent(name)}:wght@${weights}`;

    const parts: string[] = [];

    if (heading && body) {
      if (heading.toLowerCase() === body.toLowerCase()) {
        // sama → sekali aja
        parts.push(toParam(heading));
      } else {
        // beda → masuk dua-duanya
        parts.push(toParam(heading));
        parts.push(toParam(body));
      }
    } else {
      if (heading) parts.push(toParam(heading));
      if (body) parts.push(toParam(body));
    }

    return parts.length
      ? `https://fonts.googleapis.com/css2?${parts.join('&')}&display=swap`
      : null;
  };

  const headingFontForCss = themeTypography?.headingFontFamily;
  const bodyFontForCss = themeTypography?.fontFamily;

  const fontUrl = buildFontsHref(headingFontForCss, bodyFontForCss);

  return (
    <>
      {fontUrl && <link rel='stylesheet' href={fontUrl} />}
      <div className='theme_font flex min-h-screen flex-col'>
        {children}
        <style jsx global>
          {`

            .theme_layout_page_width {
              max-width: ${themeLayout.pageWidth}px;
            }
            .theme_layout_space_section {
              display: flex;
              flex-direction: column;
              row-gap: ${themeLayout.spaceSection}px;
            }
            .theme_layout_space_grid_vertical {
              column-gap: ${themeLayout.spaceGridVertical}px;
            }
            .theme_layout_space_grid_horizontal {
              row-gap: ${themeLayout.spaceGridHorizontal}px;
            }

            .theme_animation_card_hover {
              box-shadow: rgba(0, 0, 0, 0) 0px 2px 4px, rgba(0, 0, 0, 0) 0px 0px 0px;
              transition: all 300ms ease-in-out;
              cursor: pointer;
            }
            .theme_animation_card_hover:hover {
              ${
                themeAnimation?.hoverEffect === 'lift-3d'
                  ? 'box-shadow: rgba(0, 0, 0, 0.22) 0px 19px 43px, rgba(0, 0, 0, 0.18) 0px 4px 11px;'
                  : ''
              }
              ${
                themeAnimation?.hoverEffect === 'lift-3d'
                  ? 'transform: translate3d(0px, -0.5px, 0px);'
                  : ''
              }
              ${
                themeAnimation?.hoverEffect === 'lift-up' ? 'transform: translate(0px, -20px);' : ''
              }
            }

            .theme_dropdown_popup_border {
              border-width: ${themeDropdownPopup.borderWidth}px;
              border-radius: ${themeDropdownPopup.borderRadius}px;
            }
            .theme_dropdown_popup_shadow {
              box-shadow: ${themeDropdownPopup.horizontalOffset}px
                ${themeDropdownPopup.verticalOffset}px ${
            themeDropdownPopup.shadowBlur
          }px -1px rgb(0 0
                    0 / ${themeDropdownPopup.shadowOpacity / 100});
            }

            .theme_search_behavior_suggestion {
              display: ${themeSearchBehavior.enableSearchSuggestion ? 'block' : 'none'};
            }
            .theme_search_behavior_product_price {
              display: ${themeSearchBehavior.showProductPrice ? 'block' : 'none'};
            }
            .theme_search_behavior_product_vendor {
              display: ${themeSearchBehavior.showProductVendor ? 'block' : 'none'};
            }

            .theme_button {
              outline: ${themeButton.borderThickness}px solid
                rgba(0, 0, 0, ${themeButton.borderOpacity / 100}) !important;
              border-radius: ${themeButton.borderCornerRadius}px !important;
              padding: 20px !important;
              font-size: 12px !important;
              border: ${themeButton.borderThickness}px solid transparent !important;
              box-shadow: ${themeButton.shadowHorizontalOffset}px
                ${themeButton.sahdowVerticalOffset}px ${themeButton.shadowBlur}px
                rgba(0, 0, 0, ${themeButton.shadowOpacity / 100}) !important;
              webkit-box-shadow: ${themeButton.shadowHorizontalOffset}px
                ${themeButton.sahdowVerticalOffset}px ${themeButton.shadowBlur}px
                rgba(0, 0, 0, ${themeButton.shadowOpacity / 100}) !important;
              moz-box-shadow: ${themeButton.shadowHorizontalOffset}px
                ${themeButton.sahdowVerticalOffset}px ${themeButton.shadowBlur}px
                rgba(0, 0, 0, ${themeButton.shadowOpacity / 100}) !important;
              background-color: ${themeButton.colorScheme?.btnSolidBackgroud};
              color: ${themeButton.colorScheme?.btnSolidlabel};
            }
            .theme_button.outline {
              border-color: ${themeButton.colorScheme?.btnOutline};
              color: ${themeButton.colorScheme?.btnOutline};
            }

            .theme_input {
              outline: ${themeInput.borderThickness}px solid
                rgba(0, 0, 0, ${themeInput.borderOpacity / 100}) !important;
              border-radius: ${themeInput.borderCornerRadius}px !important;
              min-height: 20px;
              border: ${themeInput.borderThickness} solid transparent;
              box-shadow: ${themeInput.shadowHorizontalOffset}px ${
            themeInput.sahdowVerticalOffset
          }px
                ${themeInput.shadowBlur}px rgba(0, 0, 0, ${
            themeInput.shadowOpacity / 100
          }) !important;
              webkit-box-shadow: ${themeInput.shadowHorizontalOffset}px
                ${themeInput.sahdowVerticalOffset}px ${themeInput.shadowBlur}px
                rgba(0, 0, 0, ${themeInput.shadowOpacity / 100}) !important;
              moz-box-shadow: ${themeInput.shadowHorizontalOffset}px
                ${themeInput.sahdowVerticalOffset}px ${themeInput.shadowBlur}px
                rgba(0, 0, 0, ${themeInput.shadowOpacity / 100}) !important;
            }
          .theme_product_card {
            padding: ${themeProductCard.style === 'card' ? themeProductCard.imagePadding : 0}px;
            border-style: solid;
            border-width: ${
              themeProductCard.style === 'card' ? themeProductCard.borderThickness : 0
            }px !important;
            border-color: rgba(0, 0, 0, ${themeProductCard.borderOpacity / 100}) !important;
            --border-opacity: ${themeProductCard.borderOpacity}% !important;
            border-top-left-radius: ${themeProductCard.borderCornerRadius}px !important;
            border-top-right-radius: ${themeProductCard.borderCornerRadius}px !important;
            border-bottom-right-radius: ${
              themeProductCard.style === 'card' ? themeProductCard.borderCornerRadius : 0
            }px !important;
            border-bottom-left-radius: ${
              themeProductCard.style === 'card' ? themeProductCard.borderCornerRadius : 0
            }px !important;
            box-shadow: ${themeProductCard.shadowHorizontalOffset}px
              ${themeProductCard.sahdowVerticalOffset}px ${themeProductCard.shadowBlur}px -1px rgb(0
                  0 0 / ${themeProductCard.shadowOpacity / 100});
            background-color: ${
              themeProductCard.style === 'card'
                ? themeProductCard?.colorScheme?.background || 'white'
                : 'transparent'
            };
          }
          .theme_product_card .image_padding {
            padding: ${themeProductCard.style === 'standard' ? themeProductCard.imagePadding : 0}px;
            // background-color: ${themeProductCard?.colorScheme?.background || 'white'};
            border-style: solid;
            border-width: ${
              themeProductCard.style === 'standard' ? themeProductCard.borderThickness : 0
            }px !important;
            border-color: rgba(0, 0, 0, ${themeProductCard.borderOpacity / 100}) !important;
            border-top-right-radius: ${
              themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius : 0
            }px !important;
            border-top-left-radius: ${
              themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius : 0
            }px !important;
            border-bottom-right-radius: ${
              themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius : 0
            }px !important;
            border-bottom-left-radius: ${
              themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius : 0
            }px !important;
          }
          .theme_product_card .image_padding img {
            border-top-right-radius: ${themeProductCard.borderCornerRadius - 2}px !important;
            border-top-left-radius: ${themeProductCard.borderCornerRadius - 2}px !important;
            border-bottom-right-radius: ${
              themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius - 2 : 0
            }px !important;
            border-bottom-left-radius: ${
              themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius - 2 : 0
            }px !important;
          }
          .theme_product_card .price_product {
          --text-scale-percentage: ${((themeProductCard.fontSizePrice || 100) / 100).toFixed(1)};
          }
          .theme_product_card .text_content {
            text-align: ${themeProductCard.textAlignment};
            color: ${
              themeProductCard.style === 'standard'
                ? '#52535B'
                : themeProductCard?.colorScheme?.text || '#52535B'
            };
            padding-left: ${themeProductCard.style === 'standard' ? '0px' : '10px'};
            padding-right: ${themeProductCard.style === 'standard' ? '0px' : '10px'};
            display: flex;
            align-items: start;
            justify-content: ${themeProductCard.textAlignment};
          }
          .text_product_primary {
            color: ${themeProductCard?.colorScheme?.text || '#52535B'} !important;
          }
          .text_product_secondary {
            color: ${themeProductCard?.colorScheme?.textSecondary || '#52535B'} !important;
          }
          .theme_product_card .button {
            color: ${
              themeProductCard.style === 'standard'
                ? '#52535B'
                : themeProductCard?.colorScheme?.btnSolidlabel || '#52535B'
            } !important;
            background-color: ${themeProductCard?.colorScheme?.btnSolidBackgroud || 'white'};
          }
          .button_visibility {
             background-color: ${
               themeProductCard.buttonTransparent ? 'transparent' : ''
             } !important;
             outline: unset !important;
             box-shadow: unset !important;
             border-radius: unset !important;
             border: unset !important;
             display: ${themeProductCard.showButtonOnHover ? 'none' : 'inline-flex'};
          }
          .image_padding:hover .button_visibility {
             display: inline-flex !important;
          }
          .theme_blog_card {
            padding: ${themeBlogCard.style === 'card' ? themeBlogCard.imagePadding : 0}px;
            border-style: solid;
            border-width: ${
              themeBlogCard.style === 'card' ? themeBlogCard.borderThickness : 0
            }px !important;
            border-color: rgba(0, 0, 0, ${themeBlogCard.borderOpacity / 100}) !important;
            --border-opacity: ${themeBlogCard.borderOpacity}% !important;
            border-top-left-radius: ${themeBlogCard.borderCornerRadius}px !important;
            border-top-right-radius: ${themeBlogCard.borderCornerRadius}px !important;
            border-bottom-right-radius: ${
              themeBlogCard.style === 'card' ? themeBlogCard.borderCornerRadius : 0
            }px !important;
            border-bottom-left-radius: ${
              themeBlogCard.style === 'card' ? themeBlogCard.borderCornerRadius : 0
            }px !important;
            box-shadow: ${themeBlogCard.shadowHorizontalOffset}px
              ${themeBlogCard.sahdowVerticalOffset}px ${themeBlogCard.shadowBlur}px -1px rgb(0 0 0 /
                  ${themeBlogCard.shadowOpacity / 100});
            background-color: ${
              themeBlogCard.style === 'card'
                ? themeBlogCard?.colorScheme?.background || 'white'
                : 'transparent'
            };
            color: ${themeBlogCard?.colorScheme?.text || 'black'};

          }
          .theme_blog_card .image_padding {
            padding: ${themeBlogCard.style === 'standard' ? themeBlogCard.imagePadding : 0}px;
            background-color: ${themeBlogCard?.colorScheme?.background || 'white'};
            border-style: solid;
            border-width: ${
              themeBlogCard.style === 'standard' ? themeBlogCard.borderThickness : 0
            }px !important;
            border-color: rgba(0, 0, 0, ${themeBlogCard.borderOpacity / 100}) !important;
            border-top-right-radius: ${
              themeBlogCard.style === 'standard' ? themeBlogCard.borderCornerRadius : 0
            }px !important;
            border-top-left-radius: ${
              themeBlogCard.style === 'standard' ? themeBlogCard.borderCornerRadius : 0
            }px !important;
            border-bottom-right-radius: ${
              themeBlogCard.style === 'standard' ? themeBlogCard.borderCornerRadius : 0
            }px !important;
            border-bottom-left-radius: ${
              themeBlogCard.style === 'standard' ? themeBlogCard.borderCornerRadius : 0
            }px !important;
          }
          .theme_blog_card .image_padding img {
            border-top-right-radius: ${themeBlogCard.borderCornerRadius - 2}px !important;
            border-top-left-radius: ${themeBlogCard.borderCornerRadius - 2}px !important;
            border-bottom-right-radius: ${
              themeBlogCard.style === 'standard' ? themeBlogCard.borderCornerRadius - 2 : 0
            }px !important;
            border-bottom-left-radius: ${
              themeBlogCard.style === 'standard' ? themeBlogCard.borderCornerRadius - 2 : 0
            }px !important;
          }
          .theme_blog_card .card_title {
            font-size: ${themeBlogCard.style === 'standard' ? 14 : 16}px;
            line-height: ${themeBlogCard.style === 'standard' ? 20 : 24}px;
          }
          .theme_blog_card .text_content {
            text-align: ${themeBlogCard.textAlignment};
            color: ${
              themeBlogCard.style === 'standard'
                ? '#52535B'
                : themeBlogCard?.colorScheme?.text || '#52535B'
            } !important;
            padding-left: ${themeBlogCard.style === 'standard' ? '0px' : '12px'};
            padding-right: ${themeBlogCard.style === 'standard' ? '0px' : '12px'};
          }

            .theme_variant_pill {
              outline: ${themeVariantPill.borderThickness}px solid
                ${themeVariantPill.colorScheme?.btnOutline || '#52535B'} !important;
              border-radius: ${themeVariantPill.borderCornerRadius}px !important;
              min-height: 50px;
              margin-right: ${themeVariantPill.borderThickness * 2 + 8}px;
              border: ${themeVariantPill.borderThickness} solid transparent;
              box-shadow: ${themeVariantPill.shadowHorizontalOffset}px
                ${themeVariantPill.sahdowVerticalOffset}px ${themeVariantPill.shadowBlur}px
                rgba(0, 0, 0, ${themeVariantPill.shadowOpacity / 100}) !important;
              webkit-box-shadow: ${themeVariantPill.shadowHorizontalOffset}px
                ${themeVariantPill.sahdowVerticalOffset}px ${themeVariantPill.shadowBlur}px
                rgba(0, 0, 0, ${themeVariantPill.shadowOpacity / 100}) !important;
              moz-box-shadow: ${themeVariantPill.shadowHorizontalOffset}px
                ${themeVariantPill.sahdowVerticalOffset}px ${themeVariantPill.shadowBlur}px
                rgba(0, 0, 0, ${themeVariantPill.shadowOpacity / 100}) !important;
              color: ${themeVariantPill.colorScheme?.text || '#52535B'};
              background-color: ${themeVariantPill.colorScheme?.background || 'white'};
            }
            .theme_variant_pill[data-state='on'] {
              border: 0px;
              color: ${themeVariantPill.colorScheme?.btnSolidlabel || '#52535B'} !important;
              background-color: ${
                themeVariantPill.colorScheme?.btnSolidBackgroud || 'white'
              } !important;
            }
            .theme_variant_pill_selected {
              color: ${themeVariantPill.colorScheme?.btnSolidlabel || '#52535B'} !important;
              background-color: ${
                themeVariantPill.colorScheme?.btnSolidBackgroud || 'white'
              } !important;
            }
            .theme_variant_pill_group {
              row-gap: ${themeVariantPill.borderThickness * 2 + 16}px;
            }

            .theme_media {
              margin-left: ${themeMedia.borderThickness}px !important;
              margin-right: ${themeMedia.borderThickness}px !important;
              outline: ${themeMedia.borderThickness}px solid
                rgba(0, 0, 0, ${themeMedia.borderOpacity / 100}) !important;
              border-radius: ${themeMedia.borderCornerRadius}px !important;
              border: ${themeMedia.borderThickness} solid transparent;
              box-shadow: ${themeMedia.shadowHorizontalOffset}px ${
            themeMedia.sahdowVerticalOffset
          }px
                ${themeMedia.shadowBlur}px rgba(0, 0, 0, ${
            themeMedia.shadowOpacity / 100
          }) !important;
              webkit-box-shadow: ${themeMedia.shadowHorizontalOffset}px
                ${themeMedia.sahdowVerticalOffset}px ${themeMedia.shadowBlur}px
                rgba(0, 0, 0, ${themeMedia.shadowOpacity / 100}) !important;
              moz-box-shadow: ${themeMedia.shadowHorizontalOffset}px
                ${themeMedia.sahdowVerticalOffset}px ${themeMedia.shadowBlur}px
                rgba(0, 0, 0, ${themeMedia.shadowOpacity / 100}) !important;
            }

            .theme_collection_card {
              padding: ${themeCollection.style === 'card' ? themeCollection.imagePadding : 0}px;
              border-style: solid;
              border-width: ${
                themeCollection.style === 'card' ? themeCollection.borderThickness : 0
              }px !important;
              border-color: rgba(0, 0, 0, ${themeCollection.borderOpacity / 100}) !important;
              --border-opacity: ${themeCollection.borderOpacity}% !important;
              border-top-left-radius: ${themeCollection.borderCornerRadius}px !important;
              border-top-right-radius: ${themeCollection.borderCornerRadius}px !important;
              border-bottom-right-radius: ${
                themeCollection.style === 'card' ? themeCollection.borderCornerRadius : 0
              }px !important;
              border-bottom-left-radius: ${
                themeCollection.style === 'card' ? themeCollection.borderCornerRadius : 0
              }px !important;
              box-shadow: ${
                themeCollection.style === 'card'
                  ? `${themeCollection.shadowHorizontalOffset}px
                ${themeCollection.sahdowVerticalOffset}px ${
                      themeCollection.shadowBlur
                    }px -1px rgb(0 0
                    0 / ${themeCollection.shadowOpacity / 100});`
                  : 'none'
              }
              background-color: ${
                themeCollection.style === 'card'
                  ? themeCollection?.colorScheme?.background || 'white'
                  : 'transparent'
              };
            }

            .theme_collection_card .text_content {
              text-align: ${themeCollection.textAlignment};
              color: ${
                themeCollection.style === 'standard'
                  ? '#52535B'
                  : themeCollection?.colorScheme?.text || '#52535B'
              } !important;
              padding-left: ${themeCollection.style === 'standard' ? '0px' : '12px'};
              padding-right: ${themeCollection.style === 'standard' ? '0px' : '12px'};
              display: flex;
              --text-scale-percentage: ${((themeCollection.fontSize || 100) / 100).toFixed(1)};
              align-items: center;
              justify-content: ${themeCollection.textAlignment};
            }

            // #wrapper-footer {
            //   position: absolute;
            //   left: 0;
            // }

            #wrapper-header {
              width: 100vw;
              left: 50%;
              right: 50%;
              margin-left: -50vw;
              margin-right: -50vw;
              position: relative;
              display: flex;
              flex-direction: column;
              align-items: center;
            }

            .theme_product_card {
              padding: ${themeProductCard.style === 'card' ? themeProductCard.imagePadding : 0}px;
              border-style: solid;
              border-width: ${
                themeProductCard.style === 'card' ? themeProductCard.borderThickness : 0
              }px !important;
              border-color: rgba(0, 0, 0, ${themeProductCard.borderOpacity / 100}) !important;
              --border-opacity: ${themeProductCard.borderOpacity}% !important;
              border-top-left-radius: ${themeProductCard.borderCornerRadius}px !important;
              border-top-right-radius: ${themeProductCard.borderCornerRadius}px !important;
              border-bottom-right-radius: ${
                themeProductCard.style === 'card' ? themeProductCard.borderCornerRadius : 0
              }px !important;
              border-bottom-left-radius: ${
                themeProductCard.style === 'card' ? themeProductCard.borderCornerRadius : 0
              }px !important;
              box-shadow: ${themeProductCard.shadowHorizontalOffset}px
                ${themeProductCard.sahdowVerticalOffset}px ${
            themeProductCard.shadowBlur
          }px -1px rgb(0
                    0 0 / ${themeProductCard.shadowOpacity / 100});
              background-color: ${
                themeProductCard.style === 'card'
                  ? themeProductCard?.colorScheme?.background || 'white'
                  : 'transparent'
              };
            }
            .theme_product_card .image_padding {
              padding: ${
                themeProductCard.style === 'standard' ? themeProductCard.imagePadding : 0
              }px;
              background-color: ${themeProductCard?.colorScheme?.background || 'white'};
              border-style: solid;
              border-width: ${
                themeProductCard.style === 'standard' ? themeProductCard.borderThickness : 0
              }px !important;
              border-color: rgba(0, 0, 0, ${themeProductCard.borderOpacity / 100}) !important;
              border-top-right-radius: ${
                themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius : 0
              }px !important;
              border-top-left-radius: ${
                themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius : 0
              }px !important;
              border-bottom-right-radius: ${
                themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius : 0
              }px !important;
              border-bottom-left-radius: ${
                themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius : 0
              }px !important;
            }
            .theme_product_card .image_padding img {
              border-top-right-radius: ${themeProductCard.borderCornerRadius - 2}px !important;
              border-top-left-radius: ${themeProductCard.borderCornerRadius - 2}px !important;
              border-bottom-right-radius: ${
                themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius - 2 : 0
              }px !important;
              border-bottom-left-radius: ${
                themeProductCard.style === 'standard' ? themeProductCard.borderCornerRadius - 2 : 0
              }px !important;
            }
            .theme_product_card .card_title {
            --text-scale-prodTitle: ${((themeProductCard.fontSizeTitle || 100) / 100).toFixed(1)};
            font-size: calc(
      ${themeProductCard.style === 'standard' ? '10px' : '12px'}
      * var(--text-scale-prodTitle, 1)
    );
              line-height: ${themeProductCard.style === 'standard' ? 20 : 22}px;
              min-height: ${themeProductCard.style === 'standard' ? 32 : 38}px;
            }
            .theme_cart_note_input {
              display: ${themeCart.enableCartNote ? 'block' : 'none'};
            }
            .theme_cart_drawer_bg {
              background-color: ${themeCart.drawerColorScheme?.background || 'white'};
            }
            .theme_cart_drawer_color {
              color: ${themeCart.drawerColorScheme?.text};
            }
            .theme_cart_drawer_btn {
              background-color: ${themeCart.drawerColorScheme?.btnSolidBackgroud};
              color: ${themeCart.drawerColorScheme?.btnSolidlabel};
            }
           .theme_cart_page_bg {
              background-color: ${themeCart.pageCartColorScheme?.background || 'white'};
            }
            .theme_cart_page_color {
              color: ${themeCart.pageCartColorScheme?.text};
            }
            .theme_cart_page_btn {
              background-color: ${themeCart.pageCartColorScheme?.btnSolidBackgroud};
              color: ${themeCart.pageCartColorScheme?.btnSolidlabel};
            }
            .theme_search_behavior_product_vendor {
              display: ${themeCart.showProductVendor ? 'block' : 'none'};
            }

            .theme_badge {
              left: ${
                ['bottom_left', 'top_left'].includes(themeBadge.positionOnCard) ? '8px' : 'none'
              };
              top: ${
                ['top_left', 'top_right'].includes(themeBadge.positionOnCard) ? '8px' : 'none'
              };
              bottom: ${
                ['bottom_left', 'bottom_right'].includes(themeBadge.positionOnCard) ? '8px' : 'none'
              };
              right: ${
                ['bottom_right', 'top_right'].includes(themeBadge.positionOnCard) ? '8px' : 'none'
              };
              border-radius: ${themeBadge.borderCornerRadius}px;
            }
            .theme_badge_inside {
              border-radius: ${themeBadge.borderCornerRadius}px;
            }
            .theme_badge_sold_out {
              background-color: ${themeBadge.soldOutColorScheme?.background || 'white'};
              color: ${themeBadge.soldOutColorScheme?.text || 'black'};
            }
            .theme_badge_promotion {
              background-color: ${themeBadge.promotionColorScheme?.background || 'white'};
              color: ${themeBadge.promotionColorScheme?.text || 'black'};
            }
            .theme_badge_promotion_gradient {
                color: ${themeBadge.promotionColorGradient?.text || 'black'};
                background: linear-gradient(to right, ${
                  themeBadge.promotionColorGradient?.color1 || 'white'
                }, ${themeBadge.promotionColorGradient?.color2 || 'white'});
            }
            .theme_badge_new_arrival {
              background-color: ${themeBadge.newArrivalColorScheme?.background || 'white'};
              color: ${themeBadge.newArrivalColorScheme?.text || 'black'};
            }
            .theme_badge_new_arrival_gradient {
                color: ${themeBadge.newArrivalColorGradient?.text || 'black'};
                background: linear-gradient(to right, ${
                  themeBadge.newArrivalColorGradient?.color1 || 'white'
                }, ${themeBadge.newArrivalColorGradient?.color2 || 'white'});
            }
            .theme_badge_best_seller {
              background-color: ${themeBadge.bestSellerColorScheme?.background || 'white'};
              color: ${themeBadge.bestSellerColorScheme?.text || 'black'};
            }
            .theme_badge_best_seller_gradient {
                color: ${themeBadge.bestSellerColorGradient?.text || 'black'};
                background: linear-gradient(to right, ${
                  themeBadge.bestSellerColorGradient?.color1 || 'white'
                }, ${themeBadge.bestSellerColorGradient?.color2 || 'white'});
            }

            .theme_drawer {
              outline: ${themeDrawer.borderThickness}px solid
                rgba(0, 0, 0, ${themeDrawer.borderOpacity / 100}) !important;
              border-radius: ${themeDrawer.borderCornerRadius}px 0px 0px
                ${themeDrawer.borderCornerRadius}px !important;
              min-height: 50px !important;
              border: ${themeDrawer.borderThickness} solid transparent !important;
              box-shadow: ${themeDrawer.shadowHorizontalOffset}px
                ${themeDrawer.sahdowVerticalOffset}px ${themeDrawer.shadowBlur}px
                rgba(0, 0, 0, ${themeDrawer.shadowOpacity / 100}) !important;
              webkit-box-shadow: ${themeDrawer.shadowHorizontalOffset}px
                ${themeDrawer.sahdowVerticalOffset}px ${themeDrawer.shadowBlur}px
                rgba(0, 0, 0, ${themeDrawer.shadowOpacity / 100}) !important;
              moz-box-shadow: ${themeDrawer.shadowHorizontalOffset}px
                ${themeDrawer.sahdowVerticalOffset}px ${themeDrawer.shadowBlur}px
                rgba(0, 0, 0, ${themeDrawer.shadowOpacity / 100}) !important;
            }

            .theme_modal_product_detail {
              background-color: ${themeModalProductDetail.background} !important;
              color: ${themeModalProductDetail.text} !important;
            }
            .theme_button_modal_product_detail {
              background-color: ${themeModalProductDetail.btnSolidBackgroud} !important;
              color: ${themeModalProductDetail.btnSolidlabel} !important;
            }
            .theme_button_modal_product_detail:hover {
              opacity: 0.5;
            }
            .theme_button_outline_modal_product_detail {
              background-color: ${themeModalProductDetail.btnSolidlabel} !important;
              color: ${themeModalProductDetail.btnSolidBackgroud} !important;
              border-color: ${themeModalProductDetail.btnSolidBackgroud} !important;
            }

            .theme_font {
              font-family: ${bodyFontForCss};
              font-weight: ${variantValue.weight};
              font-style: ${variantValue.style};
              --text-scale-percentage: ${((themeTypography.fontScaleSize || 100) / 110).toFixed(1)};
            }
            .theme_font_heading {
              font-family: ${headingFontForCss};
              font-weight: ${headingVariantValue.weight};
              font-style: ${headingVariantValue.style};
              --text-scale-percentage: ${(
                (themeTypography.headingFontScaleSize || 100) / 110
              ).toFixed(1)};
            }
          `}
        </style>
      </div>
    </>
  );
};
